import React from 'react';
import { initTeams } from '../teams.js';
import { ME } from './data.jsx';
import { Button, Pill, SegmentedControl } from './ds/fgcp-ds.jsx';

/**
 * Teams 기능 시험실 — 탭 쪽(TeamsJS) 기능(계획 문서 5.8). Teams 탭 안·시스템 관리자만(셸이 거른다).
 * 기능마다 이 클라이언트에서 되는지(isSupported)와 결과를 보인다. 업무 데이터는 쓰지 않는다.
 * 봇 쪽 기능(카드·스트리밍·피드 등)은 봇 1:1 대화에 '기능 시험'.
 */
const { useState: uS, useEffect: uE } = React;
const BASE = () => window.location.origin;

/** 시험 목록 — 이름, 쓰는 TeamsJS 기능(isSupported로 확인), 실행 */
const TESTS = [
  { id: 'context', title: '문맥(getContext)', cap: null, run: (sdk) => sdk.app.getContext().then(c => ({ host: c.app?.host?.name, client: c.app?.host?.clientType, frame: c.page?.frameContext, subPageId: c.page?.subPageId, theme: c.app?.theme, locale: c.app?.locale })) },
  { id: 'chat', title: '채팅 열기(나에게, 메시지 채움)', cap: 'chat', run: (sdk) => sdk.chat.openChat({ user: ME.email, message: 'Teams 시험실 채팅 열기' }) },
  { id: 'call', title: '통화 시작(나에게 음성)', cap: 'call', run: (sdk) => sdk.call.startCall({ targets: [ME.email], requestedModalities: [sdk.call.CallModalities.Audio] }) },
  { id: 'people', title: '사람 선택', cap: 'people', run: (sdk) => sdk.people.selectPeople({ singleSelect: false, openOrgWideSearchInChatOrChannel: true }).then(ps => ps.map(p => p.displayName)) },
  { id: 'share', title: '웹 공유(Workspace 홈)', cap: 'sharing', run: (sdk) => sdk.sharing.shareWebContent({ content: [{ type: 'URL', url: `${BASE()}/`, message: 'Workspace', preview: true }] }) },
  { id: 'barcode', title: '바코드 읽기(모바일)', cap: 'barCode', run: (sdk) => sdk.barCode.scanBarCode({ timeOutIntervalInSec: 30 }) },
  { id: 'location', title: '내 위치(모바일)', cap: 'geoLocation', run: (sdk) => sdk.geoLocation.getCurrentLocation().then(l => ({ lat: Number(l.latitude).toFixed(3), lon: Number(l.longitude).toFixed(3) })) },
  { id: 'media', title: '사진 고르기', cap: 'media', run: (sdk) => sdk.media.selectMedia({ mediaType: sdk.media.MediaType.Image, maxMediaCount: 1 }).then(m => m.map(x => ({ format: x.format, size: x.size }))) },
  { id: 'profile', title: '내 프로필 카드', cap: 'profile', run: (sdk, el) => sdk.profile.showProfile({ modality: 'Card', triggerType: 'MouseClick', persona: { identifiers: { Smtp: ME.email, Upn: ME.email }, displayName: ME.name }, targetElementBoundingRect: el.getBoundingClientRect() }) },
  { id: 'install', title: '앱 설치 창(이 앱)', cap: 'appInstallDialog', run: (sdk) => sdk.app.getContext().then(c => sdk.appInstallDialog.openAppInstallDialog({ appId: c.app?.appId?.toString?.() ?? c.app?.appId })) },
  { id: 'stage', title: '무대 보기(Workspace 홈)', cap: 'stageView', run: (sdk) => sdk.app.getContext().then(c => sdk.stageView.open({ appId: c.app?.appId?.toString?.() ?? c.app?.appId, contentUrl: `${BASE()}/?inTeams=1`, websiteUrl: `${BASE()}/`, title: 'Workspace', entityId: 'workspace', openMode: 'modal' })) },
  { id: 'dialogUrl', title: '웹 대화창(휴가)', cap: 'dialog', run: (sdk) => new Promise(ok => sdk.dialog.url.open({ url: `${BASE()}/?inTeams=1&dialog=1#/leave`, size: { height: 'large', width: 'large' }, title: '휴가' }, r => ok(r ?? '닫힘'))) },
  { id: 'dialogCard', title: '카드 대화창', cap: 'dialog', run: (sdk) => new Promise(ok => sdk.dialog.adaptiveCard.open({ title: '카드 대화창', size: { height: 'small', width: 'medium' },
    card: JSON.stringify({ type: 'AdaptiveCard', version: '1.5', body: [{ type: 'Input.Text', id: 'note', label: '아무 글' }], actions: [{ type: 'Action.Submit', title: '보내기' }] }) }, r => ok(r ?? '닫힘'))) },
  { id: 'deeplink', title: '딥링크 복사(이 화면)', cap: 'pages', run: (sdk) => sdk.pages.shareDeepLink({ subPageId: 'teams-lab', subPageLabel: 'Teams 시험실' }) },
  { id: 'navigate', title: '탭 안 이동(결재 화면)', cap: 'pages', run: (sdk) => sdk.pages.currentApp.navigateTo({ pageId: 'workspace', subPageId: 'approval' }) },
  { id: 'mail', title: '메일 쓰기(Outlook만)', cap: 'mail', run: (sdk) => sdk.mail.composeMail({ type: sdk.mail.ComposeMailType.New, toRecipients: [ME.email], subject: '시험', message: 'Teams 시험실' }) },
  { id: 'calendar', title: '회의 만들기(Outlook만)', cap: 'calendar', run: (sdk) => sdk.calendar.composeMeeting({ attendees: [ME.email], subject: '시험 회의' }) }
];

export function TeamsLab({ onToast }) {
  const [sdk, setSdk] = uS(null);
  const [results, setResults] = uS({});
  uE(() => { initTeams().then(setSdk).catch(e => onToast('Teams SDK를 불러오지 못했습니다 — ' + e.message)); }, [onToast]);
  const supported = (cap) => {
    if (!sdk || !cap) return true;
    const ns = sdk[cap];
    try { return Boolean(ns && (typeof ns.isSupported !== 'function' || ns.isSupported())); } catch { return false; }
  };
  const run = (t, el) => {
    setResults(r => ({ ...r, [t.id]: { state: 'run' } }));
    Promise.resolve().then(() => t.run(sdk, el))
      .then(v => setResults(r => ({ ...r, [t.id]: { state: 'ok', value: v === undefined ? '완료' : v } })))
      .catch(e => setResults(r => ({ ...r, [t.id]: { state: 'err', value: e?.message ?? String(e) } })));
  };
  return (
    <React.Fragment>
      <div className="head"><h1>Teams 시험실</h1><div className="r"><Pill tone="info" dot={false}>시스템 관리자</Pill></div></div>
      <div className="body" style={{ paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>탭 쪽(TeamsJS) 기능입니다. 봇 쪽 기능(카드·스트리밍·피드 등)은 봇 1:1 대화에 '기능 시험'.</div>
        {TESTS.map(t => {
          const ok = supported(t.cap); const r = results[t.id];
          return (
            <div key={t.id} data-teams-lab={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.title}</div>
                {r && <div className="mono" style={{ fontSize: 11, color: r.state === 'err' ? 'var(--crit)' : 'var(--ink-3)', wordBreak: 'break-all' }}>
                  {r.state === 'run' ? '실행 중…' : typeof r.value === 'string' ? r.value : JSON.stringify(r.value)}</div>}
              </div>
              <Pill tone={ok ? 'ok' : 'neutral'} dot={false}>{ok ? '지원' : '이 클라이언트 미지원'}</Pill>
              <Button size="sm" variant="secondary" disabled={!sdk} onClick={e => run(t, e.currentTarget)}>시험</Button>
            </div>
          );
        })}
      </div>
    </React.Fragment>
  );
}

/** 팀·그룹 채팅·회의 탭 구성 — 탭으로 둘 Workspace 화면 하나를 고른다(계획 문서 5.8 T6) */
const TAB_VIEWS = [
  { value: 'board', label: '게시판·공지' }, { value: 'booking', label: '회의실 예약' }, { value: 'org', label: '조직도' }, { value: 'approval', label: '전자결재' }
];
export function TeamsTabConfig() {
  const [view, setView] = uS('board');
  uE(() => {
    let alive = true;
    initTeams().then(sdk => {
      if (!alive) return;
      sdk.pages.config.registerOnSaveHandler(ev => {
        const label = TAB_VIEWS.find(v => v.value === view)?.label ?? 'Workspace';
        sdk.pages.config.setConfig({ entityId: `workspace-${view}`, contentUrl: `${BASE()}/?inTeams=1#/${view}`, websiteUrl: `${BASE()}/#/${view}`, suggestedDisplayName: label })
          .then(() => ev.notifySuccess()).catch(e => ev.notifyFailure(e?.message ?? String(e)));
      });
      sdk.pages.config.setValidityState(true);
    });
    return () => { alive = false; };
  }, [view]);
  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, background: 'var(--bg)', minHeight: '100vh' }}>
      <h2 style={{ margin: 0 }}>Workspace 탭</h2>
      <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>이 대화에 탭으로 둘 Workspace 화면을 고르세요. 보이는 내용은 탭을 여는 사람의 권한을 따릅니다.</div>
      <SegmentedControl aria-label="탭 화면" value={view} onChange={setView} options={TAB_VIEWS} />
    </div>
  );
}
